import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
  Container
} from '@mui/material';
import {
  MeetingRoom as RoomIcon,
  Group as TeamIcon,
  Build as EquipmentIcon,
  LocalHospital as SurgeryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled Components - Purple/Blue Gradient Header
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
}));

const SurgeryDashboard = () => {
  const [stats] = useState({
    totalRooms: 8,
    availableRooms: 5,
    totalSurgeries: 156,
    completedSurgeries: 142,
    pendingSurgeries: 14,
    equipmentCount: 245,
    maintenanceDue: 12,
    teamMembers: 45,
    activeTeams: 8
  });

  const StatCard = ({ title, value, icon, color, trend, subtitle }) => (
    <Card sx={{ 
      height: '100%', 
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}30`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 25px ${color}20`,
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>
            {icon}
          </Avatar>
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {trend > 0 ? <TrendingUpIcon color="success" /> : <TrendingDownIcon color="error" />}
              <Typography variant="caption" color={trend > 0 ? 'success.main' : 'error.main'}>
                {Math.abs(trend)}%
              </Typography>
            </Box>
          )}
        </Box>
        <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <HeaderCard>
        <CardContent>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Surgery Dashboard
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Comprehensive overview of surgical facility performance and statistics
          </Typography>
        </CardContent>
      </HeaderCard>

      {/* Stats Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Operating Rooms"
            value={`${stats.availableRooms}/${stats.totalRooms}`}
            icon={<RoomIcon />}
            color="#708bff"
            trend={5}
            subtitle="Available"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Surgeries"
            value={stats.totalSurgeries}
            icon={<SurgeryIcon />}
            color="#4caf50"
            trend={12}
            subtitle={`${stats.completedSurgeries} completed`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Equipment"
            value={stats.equipmentCount}
            icon={<EquipmentIcon />}
            color="#ff9800"
            trend={-3}
            subtitle={`${stats.maintenanceDue} need maintenance`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Surgical Teams"
            value={stats.teamMembers}
            icon={<TeamIcon />}
            color="#9c27b0"
            trend={8}
            subtitle={`${stats.activeTeams} active teams`}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default SurgeryDashboard; 